import { defineQuery, addEntity, removeEntity } from 'bitecs';
import { Animal, Plant, Position, Tile, Corpse, AI_STATE, animalQuery, plantQuery, corpseQuery, tileQuery } from '../world.js';
import { TILE_SIZE, CHUNK_SIZE, CURRENT_MODE } from '../config.js';
import { entityValidator } from './entityValidator.js';

// Ecosystem balance constants
const ECOSYSTEM_CONFIG = {
    // Population controls per 1000 tiles
    MAX_POPULATION_DENSITY: {
        RABBIT: 0.8,      // 8 rabbits per 1000 tiles
        CHICKEN: 0.6,     // 6 chickens per 1000 tiles
        WOLF: 0.1,        // 1 wolf per 1000 tiles
        HAWK: 0.05,       // 1 hawk per 2000 tiles
        DEER: 0.4         // 4 deer per 1000 tiles
    },
    
    // Reproduction rates (per year)
    REPRODUCTION_RATE: {
        RABBIT: 6,        // 6 litters per year
        CHICKEN: 12,      // 12 broods per year
        WOLF: 1,          // 1 litter per year
        HAWK: 1,          // 1 brood per year
        DEER: 1           // 1 fawn per year
    },
    
    // Predation rates
    PREDATION_SUCCESS: {
        WOLF_RABBIT: 0.3, // 30% success rate
        HAWK_CHICKEN: 0.4, // 40% success rate
        WOLF_DEER: 0.15   // 15% success rate
    },
    
    // Natural death rates (per year)
    NATURAL_MORTALITY: {
        RABBIT: 0.7,      // 70% die naturally per year
        CHICKEN: 0.5,     // 50% die naturally per year
        WOLF: 0.3,        // 30% die naturally per year
        HAWK: 0.2,        // 20% die naturally per year
        DEER: 0.4         // 40% die naturally per year
    }
};

// Performance optimization constants
const PERFORMANCE_CONFIG = {
    ACTIVE_RADIUS: 15,        // Only process entities within 15 chunks
    CHUNK_PROCESSING_RATE: 4, // Process 4 chunks per frame
    MAX_ENTITIES_PER_FRAME: 30,
    LOD_DISTANCES: {
        HIGH: 5,   // Full detail within 5 chunks
        MEDIUM: 10, // Reduced detail 5-10 chunks
        LOW: 15    // Minimal detail 10-15 chunks
    }
};

// Chunk-based entity management
class ChunkManager {
    constructor() {
        this.chunks = new Map();
        this.activeChunks = new Set();
        this.processingQueue = [];
        this.lastProcessedChunk = 0;
    }
    
    getChunkKey(x, y) {
        const chunkX = Math.floor(x / (CHUNK_SIZE * TILE_SIZE));
        const chunkY = Math.floor(y / (CHUNK_SIZE * TILE_SIZE));
        return `${chunkX},${chunkY}`;
    }
    
    updateChunk(chunkKey, entities) {
        this.chunks.set(chunkKey, {
            entities: entities || [],
            lastUpdate: Date.now(),
            populationDensity: entities ? entities.length : 0
        });
    }
    
    getActiveChunks(playerX, playerY) {
        const playerChunkX = Math.floor(playerX / (CHUNK_SIZE * TILE_SIZE));
        const playerChunkY = Math.floor(playerY / (CHUNK_SIZE * TILE_SIZE));
        const activeChunks = new Set();
        
        for (let dx = -PERFORMANCE_CONFIG.ACTIVE_RADIUS; dx <= PERFORMANCE_CONFIG.ACTIVE_RADIUS; dx++) {
            for (let dy = -PERFORMANCE_CONFIG.ACTIVE_RADIUS; dy <= PERFORMANCE_CONFIG.ACTIVE_RADIUS; dy++) {
                const chunkKey = `${playerChunkX + dx},${playerChunkY + dy}`;
                activeChunks.add(chunkKey);
            }
        }
        
        return activeChunks;
    }
    
    getLODLevel(distance) {
        if (distance <= PERFORMANCE_CONFIG.LOD_DISTANCES.HIGH) return 'HIGH';
        if (distance <= PERFORMANCE_CONFIG.LOD_DISTANCES.MEDIUM) return 'MEDIUM';
        return 'LOW';
    }
}

// Population balance manager
class PopulationManager {
    constructor() {
        this.populationHistory = new Map();
        this.lastCensus = Date.now();
        this.censusInterval = 30000; // Census every 30 seconds
    }
    
    conductCensus(world) {
        const census = {
            totalAnimals: 0,
            totalPlants: 0,
            byType: {},
            chunks: new Map()
        };
        
        // Count animals by type
        const animals = animalQuery(world);
        for (const animal of animals) {
            census.totalAnimals++;
            // Note: You'll need to add Animal.type[animal] to world.js
            const type = 'RABBIT'; // Placeholder - get from animal component
            census.byType[type] = (census.byType[type] || 0) + 1;
        }
        
        // Count plants
        const plants = plantQuery(world);
        census.totalPlants = plants.length;
        
        this.populationHistory.set(Date.now(), census);
        this.lastCensus = Date.now();
        
        return census;
    }
    
    shouldSpawnEntity(type, currentCount, landTileCount) {
        const maxPop = ECOSYSTEM_CONFIG.MAX_POPULATION_DENSITY[type] * (landTileCount / 1000);
        return currentCount < maxPop;
    }
    
    shouldCullEntity(type, currentCount, landTileCount) {
        const maxPop = ECOSYSTEM_CONFIG.MAX_POPULATION_DENSITY[type] * (landTileCount / 1000) * 1.5;
        return currentCount > maxPop;
    }
}

// Ecosystem interactions
class EcosystemInteractions {
    constructor() {
        this.predationEvents = [];
        this.reproductionEvents = [];
    }
    
    processPredation(world, animals) {
        for (const predator of animals) {
            if (Animal.type[predator] === 'WOLF' || Animal.type[predator] === 'HAWK') {
                const prey = this.findNearbyPrey(world, predator);
                if (prey && Math.random() < this.getPredationChance(predator, prey)) {
                    this.executePredation(world, predator, prey);
                }
            }
        }
    }
    
    findNearbyPrey(world, predator) {
        const predatorX = Animal.x[predator];
        const predatorY = Animal.y[predator];
        const searchRadius = 3 * TILE_SIZE;
        
        const animals = animalQuery(world);
        for (const animal of animals) {
            if (animal === predator) continue;
            
            const distance = Math.sqrt(
                Math.pow(Animal.x[animal] - predatorX, 2) + 
                Math.pow(Animal.y[animal] - predatorY, 2)
            );
            
            if (distance <= searchRadius && this.isPreyFor(predator, animal)) {
                return animal;
            }
        }
        return null;
    }
    
    isPreyFor(predator, prey) {
        const predatorType = Animal.type[predator];
        const preyType = Animal.type[prey];
        
        return (predatorType === 'WOLF' && (preyType === 'RABBIT' || preyType === 'DEER')) ||
               (predatorType === 'HAWK' && preyType === 'CHICKEN');
    }
    
    getPredationChance(predator, prey) {
        const predatorType = Animal.type[predator];
        const preyType = Animal.type[prey];
        
        if (predatorType === 'WOLF' && preyType === 'RABBIT') {
            return ECOSYSTEM_CONFIG.PREDATION_SUCCESS.WOLF_RABBIT;
        }
        if (predatorType === 'HAWK' && preyType === 'CHICKEN') {
            return ECOSYSTEM_CONFIG.PREDATION_SUCCESS.HAWK_CHICKEN;
        }
        if (predatorType === 'WOLF' && preyType === 'DEER') {
            return ECOSYSTEM_CONFIG.PREDATION_SUCCESS.WOLF_DEER;
        }
        return 0;
    }
    
    executePredation(world, predator, prey) {
        // Create corpse
        const corpse = addEntity(world);
        Corpse.type[corpse] = Animal.type[prey];
        Corpse.x[corpse] = Animal.x[prey];
        Corpse.y[corpse] = Animal.y[prey];
        Corpse.foodAmount[corpse] = 50; // Food value
        Corpse.decayRate[corpse] = 0.1;
        Corpse.age[corpse] = 0;
        
        // Remove prey
        removeEntity(world, prey);
        
        // Update predator state
        AI.state[predator] = AI_STATE.EATING;
        Animal.energy[predator] = Math.min(Animal.energy[predator] + 30, Animal.maxEnergy[predator]);
        
        this.predationEvents.push({
            type: 'predation',
            predator: Animal.type[predator],
            prey: Animal.type[prey],
            timestamp: Date.now()
        });
    }
    
    processReproduction(world, animals) {
        for (const animal of animals) {
            if (this.canReproduce(animal)) {
                this.attemptReproduction(world, animal);
            }
        }
    }
    
    canReproduce(animal) {
        return Animal.energy[animal] > 70 && // Good energy level
               Animal.age[animal] > 1000 && // Mature enough
               Math.random() < 0.001; // Small chance per tick
    }
    
    attemptReproduction(world, parent) {
        const offspring = addEntity(world);
        
        // Inherit parent's position with slight offset
        Animal.x[offspring] = Animal.x[parent] + (Math.random() - 0.5) * TILE_SIZE;
        Animal.y[offspring] = Animal.y[parent] + (Math.random() - 0.5) * TILE_SIZE;
        Animal.type[offspring] = Animal.type[parent];
        Animal.age[offspring] = 0;
        Animal.energy[offspring] = 50;
        Animal.maxEnergy[offspring] = Animal.maxEnergy[parent];
        
        // Cost to parent
        Animal.energy[parent] -= 20;
        
        this.reproductionEvents.push({
            type: 'reproduction',
            animalType: Animal.type[parent],
            timestamp: Date.now()
        });
    }
}

// Main ecosystem system
export class EcosystemSystem {
    constructor() {
        this.chunkManager = new ChunkManager();
        this.populationManager = new PopulationManager();
        this.interactions = new EcosystemInteractions();
        this.frameCount = 0;
        this.lastCensus = 0;
    }
    
    update(world, deltaTime, playerX, playerY) {
        if (!CURRENT_MODE.animals && !CURRENT_MODE.plants) return;
        
        this.frameCount++;
        
        // Handle optional parameters
        if (typeof playerX === 'undefined' || typeof playerY === 'undefined') {
            // Get player position if not provided
            const players = world.query ? world.query([Player]) : [];
            if (players.length > 0) {
                playerX = Position.x[players[0]];
                playerY = Position.y[players[0]];
            } else {
                return; // No player found, skip update
            }
        }
        
        // Update active chunks
        const activeChunks = this.chunkManager.getActiveChunks(playerX, playerY);
        
        // Process entities in active chunks only
        this.processActiveChunks(world, activeChunks);
        
        // Conduct census periodically
        if (Date.now() - this.lastCensus > this.populationManager.censusInterval) {
            const census = this.populationManager.conductCensus(world);
            this.balancePopulations(world, census);
            this.lastCensus = Date.now();
        }
        
        // Process ecosystem interactions
        if (this.frameCount % 60 === 0) { // Every second
            this.processInteractions(world);
        }
    }
    
    processActiveChunks(world, activeChunks) {
        const animals = animalQuery(world);
        const plants = plantQuery(world);
        
        // Validate entities and filter by active chunks
        const validAnimals = animals.filter(animal => {
            // Validate entity components
            entityValidator.validateEntity(animal);
            
            const chunkKey = this.chunkManager.getChunkKey(Animal.x[animal], Animal.y[animal]);
            return activeChunks.has(chunkKey);
        });
        
        const validPlants = plants.filter(plant => {
            // Validate entity components
            entityValidator.validateEntity(plant);
            
            const chunkKey = this.chunkManager.getChunkKey(Plant.x[plant], Plant.y[plant]);
            return activeChunks.has(chunkKey);
        });
        
        // Process with LOD
        this.processAnimalsWithLOD(world, validAnimals);
        this.processPlantsWithLOD(world, validPlants);
    }
    
    processAnimalsWithLOD(world, animals) {
        // Process animals with Level of Detail based on player distance
        for (const animal of animals) {
            // Get animal type for species-specific behavior
            const animalType = Animal.type[animal];
            const species = this.getAnimalSpecies(animalType);
            
            // Apply species-specific behaviors
            this.processAnimalBehavior(world, animal, species);
            
            // Energy and survival mechanics
            this.processAnimalSurvival(world, animal, species);
        }
    }
    
    getAnimalSpecies(typeId) {
        // Map type IDs to species names
        const speciesMap = {
            1: 'RABBIT',
            2: 'CHICKEN', 
            3: 'WOLF',
            4: 'HAWK',
            5: 'DEER',
            6: 'GOAT'
        };
        return speciesMap[typeId] || 'UNKNOWN';
    }
    
    processAnimalBehavior(world, animal, species) {
        switch (species) {
            case 'RABBIT':
                this.processRabbitBehavior(world, animal);
                break;
            case 'WOLF':
                this.processWolfBehavior(world, animal);
                break;
            case 'HAWK':
                this.processHawkBehavior(world, animal);
                break;
            case 'DEER':
                this.processDeerBehavior(world, animal);
                break;
            case 'CHICKEN':
                this.processChickenBehavior(world, animal);
                break;
            case 'GOAT':
                this.processGoatBehavior(world, animal);
                break;
        }
    }
    
    processRabbitBehavior(world, rabbit) {
        // Rabbits are cautious and fast, flee from predators
        const energy = Animal.energy[rabbit];
        
        // Rabbits move more when energy is high - increased frequency
        if (energy > 50 && Math.random() < 0.6) {
            // Quick hopping movement - more noticeable
            const oldX = Animal.x[rabbit];
            const oldY = Animal.y[rabbit];
            
            Animal.x[rabbit] += (Math.random() - 0.5) * TILE_SIZE * 3;
            Animal.y[rabbit] += (Math.random() - 0.5) * TILE_SIZE * 3;
            Animal.energy[rabbit] -= 0.5; // Movement costs energy
            
            // Log movement for visibility
            if (Math.random() < 0.1) {
                console.log('[ECOSYSTEM] Rabbit hopped from', oldX.toFixed(0), oldY.toFixed(0), 'to', Animal.x[rabbit].toFixed(0), Animal.y[rabbit].toFixed(0));
            }
        }
        
        // Rabbits forage for food - more frequent
        if (energy < 70 && Math.random() < 0.4) {
            Animal.energy[rabbit] += 8; // Found some food
            console.log('[ECOSYSTEM] Rabbit found food! Energy:', Animal.energy[rabbit].toFixed(1));
        }
        
        // Rabbits reproduce when conditions are good
        if (energy > 80 && Math.random() < 0.05) {
            console.log('[ECOSYSTEM] Rabbit is ready to reproduce!');
        }
    }
    
    processWolfBehavior(world, wolf) {
        // Wolves are predators that hunt in packs
        const energy = Animal.energy[wolf];
        
        // Wolves hunt when hungry - more aggressive
        if (energy < 60 && Math.random() < 0.6) {
            // Hunt for prey (simplified - just move more)
            const oldX = Animal.x[wolf];
            const oldY = Animal.y[wolf];
            
            Animal.x[wolf] += (Math.random() - 0.5) * TILE_SIZE * 2;
            Animal.y[wolf] += (Math.random() - 0.5) * TILE_SIZE * 2;
            Animal.energy[wolf] -= 1; // Hunting costs energy
            
            // Log hunting activity
            if (Math.random() < 0.2) {
                console.log('[ECOSYSTEM] Wolf hunting! Energy:', energy.toFixed(1));
            }
            
            // Successful hunt restores energy - more frequent
            if (Math.random() < 0.2) {
                Animal.energy[wolf] += 30;
                console.log('[ECOSYSTEM] 🐺 Wolf successfully hunted! Energy restored to:', Animal.energy[wolf].toFixed(1));
            }
        }
        
        // Wolves howl occasionally (atmospheric effect)
        if (Math.random() < 0.02) {
            console.log('[ECOSYSTEM] 🐺 Wolf howls in the distance!');
        }
        
        // Wolves rest when well-fed
        if (energy > 80 && Math.random() < 0.3) {
            console.log('[ECOSYSTEM] Wolf is resting. Energy:', energy.toFixed(1));
        }
    }
    
    processHawkBehavior(world, hawk) {
        // Hawks are aerial predators
        const energy = Animal.energy[hawk];
        
        // Hawks soar and dive for prey
        if (energy > 40 && Math.random() < 0.3) {
            // Soaring movement (more vertical)
            Animal.x[hawk] += (Math.random() - 0.5) * TILE_SIZE * 3;
            Animal.y[hawk] += (Math.random() - 0.5) * TILE_SIZE * 0.5;
            Animal.energy[hawk] -= 0.3;
        }
        
        // Hawks hunt small animals
        if (energy < 70 && Math.random() < 0.2) {
            // Dive for prey
            Animal.y[hawk] += TILE_SIZE; // Dive down
            Animal.energy[hawk] -= 0.5;
            
            if (Math.random() < 0.15) {
                Animal.energy[hawk] += 20;
                console.log('[ECOSYSTEM] Hawk caught prey!');
            }
        }
    }
    
    processDeerBehavior(world, deer) {
        // Deer are graceful and alert, flee from danger
        const energy = Animal.energy[deer];
        
        // Deer graze and move peacefully
        if (energy > 60 && Math.random() < 0.2) {
            // Gentle grazing movement
            Animal.x[deer] += (Math.random() - 0.5) * TILE_SIZE * 1.2;
            Animal.y[deer] += (Math.random() - 0.5) * TILE_SIZE * 1.2;
            Animal.energy[deer] -= 0.3;
        }
        
        // Deer forage for plants
        if (energy < 80 && Math.random() < 0.3) {
            Animal.energy[deer] += 8; // Found good grazing
        }
        
        // Deer are skittish and flee easily
        if (Math.random() < 0.05) {
            // Sudden movement - startled
            Animal.x[deer] += (Math.random() - 0.5) * TILE_SIZE * 4;
            Animal.y[deer] += (Math.random() - 0.5) * TILE_SIZE * 4;
            Animal.energy[deer] -= 2;
        }
    }
    
    processChickenBehavior(world, chicken) {
        // Chickens are social and peck around
        const energy = Animal.energy[chicken];
        
        // Chickens peck and scratch
        if (energy > 30 && Math.random() < 0.4) {
            // Small, quick movements
            Animal.x[chicken] += (Math.random() - 0.5) * TILE_SIZE * 0.8;
            Animal.y[chicken] += (Math.random() - 0.5) * TILE_SIZE * 0.8;
            Animal.energy[chicken] -= 0.2;
        }
        
        // Chickens find seeds and insects
        if (energy < 90 && Math.random() < 0.5) {
            Animal.energy[chicken] += 3; // Found food
        }
        
        // Chickens cluck occasionally
        if (Math.random() < 0.02) {
            console.log('[ECOSYSTEM] Chicken clucks');
        }
    }
    
    processGoatBehavior(world, goat) {
        // Goats are curious and stubborn climbers
        const energy = Animal.energy[goat];
        
        // Goats explore and climb
        if (energy > 50 && Math.random() < 0.3) {
            // Goats can move more erratically
            Animal.x[goat] += (Math.random() - 0.5) * TILE_SIZE * 1.8;
            Animal.y[goat] += (Math.random() - 0.5) * TILE_SIZE * 1.8;
            Animal.energy[goat] -= 0.4;
        }
        
        // Goats eat almost anything
        if (energy < 85 && Math.random() < 0.6) {
            Animal.energy[goat] += 6; // Found something to eat
        }
        
        // Goats are stubborn
        if (Math.random() < 0.03) {
            // Stand still for a moment (stubborn behavior)
            Animal.energy[goat] += 1; // Rest
        }
    }
    
    processAnimalSurvival(world, animal, species) {
        const energy = Animal.energy[animal];
        
        // Energy depletion based on species
        const depletionRates = {
            'RABBIT': 0.1,    // High metabolism
            'WOLF': 0.15,     // Hunter
            'HAWK': 0.12,     // Aerial hunter
            'DEER': 0.08,     // Grazer
            'CHICKEN': 0.05,  // Small bird
            'GOAT': 0.07      // Hardy browser
        };
        
        const depletion = depletionRates[species] || 0.1;
        Animal.energy[animal] -= depletion;
        
        // Natural death from starvation
        if (energy <= 0) {
            console.log(`[ECOSYSTEM] ${species} died of starvation`);
            Animal.energy[animal] = 0;
            // Could create corpse entity here
        }
        
        // Aging system
        Animal.age[animal] += 1;
        const maxAge = this.getMaxAge(species);
        if (Animal.age[animal] > maxAge) {
            console.log(`[ECOSYSTEM] ${species} died of old age`);
            Animal.energy[animal] = 0;
        }
        
        // Reproduction when conditions are right
        if (energy > 70 && Animal.age[animal] > 100 && Math.random() < 0.001) {
            this.attemptReproduction(world, animal, species);
        }
    }
    
    getMaxAge(species) {
        // Different species have different lifespans (in ticks)
        const lifespans = {
            'RABBIT': 5000,    // Short-lived
            'WOLF': 8000,      // Medium-lived
            'HAWK': 7000,      // Medium-lived
            'DEER': 9000,      // Longer-lived
            'CHICKEN': 4000,   // Short-lived
            'GOAT': 7500       // Medium-lived
        };
        return lifespans[species] || 5000;
    }
    
    attemptReproduction(world, parent, species) {
        // Simple reproduction - just increase energy cost
        const reproductionCost = 30;
        if (Animal.energy[parent] > reproductionCost) {
            Animal.energy[parent] -= reproductionCost;
            console.log(`[ECOSYSTEM] ${species} reproduced!`);
            
            // In a full implementation, this would create a new entity
            // For now, just log the event
        }
    }
    
    processPlantsWithLOD(world, plants) {
        // Process plants with Level of Detail based on player distance
        for (const plant of plants) {
            // Get plant type for species-specific behavior
            const plantType = Plant.type[plant];
            const species = this.getPlantSpecies(plantType);
            
            // Apply species-specific plant behaviors
            this.processPlantBehavior(world, plant, species);
            
            // Growth and reproduction mechanics
            this.processPlantGrowth(world, plant, species);
        }
    }
    
    getPlantSpecies(typeId) {
        // Map type IDs to plant species names
        const speciesMap = {
            1: 'TREE',
            2: 'BUSH',
            3: 'FLOWER',
            4: 'MUSHROOM',
            5: 'HERB',
            6: 'CACTUS'
        };
        return speciesMap[typeId] || 'UNKNOWN';
    }
    
    processPlantBehavior(world, plant, species) {
        switch (species) {
            case 'TREE':
                this.processTreeBehavior(world, plant);
                break;
            case 'BUSH':
                this.processBushBehavior(world, plant);
                break;
            case 'FLOWER':
                this.processFlowerBehavior(world, plant);
                break;
            case 'MUSHROOM':
                this.processMushroomBehavior(world, plant);
                break;
            case 'HERB':
                this.processHerbBehavior(world, plant);
                break;
            case 'CACTUS':
                this.processCactusBehavior(world, plant);
                break;
        }
    }
    
    processTreeBehavior(world, tree) {
        // Trees grow slowly and provide shelter
        if (Plant.growth && Plant.growth[tree] !== undefined) {
            const oldGrowth = Plant.growth[tree];
            // Trees grow faster for visibility
            Plant.growth[tree] = Math.min(Plant.growth[tree] + 0.002, 1.0);
            
            // Log growth occasionally
            if (Math.random() < 0.01 && Plant.growth[tree] > oldGrowth) {
                console.log('[ECOSYSTEM] 🌳 Tree growing! Growth:', (Plant.growth[tree] * 100).toFixed(1) + '%');
            }
            
            // Trees drop seeds occasionally
            if (Plant.growth[tree] > 0.8 && Math.random() < 0.005) {
                console.log('[ECOSYSTEM] 🌳 Tree dropped seeds!');
            }
        }
        
        // Trees provide oxygen (atmospheric effect)
        if (Math.random() < 0.001) {
            console.log('[ECOSYSTEM] 🌳 Tree producing oxygen...');
        }
    }
    
    processBushBehavior(world, bush) {
        // Bushes grow moderately and provide berries
        if (Plant.growth && Plant.growth[bush] !== undefined) {
            const oldGrowth = Plant.growth[bush];
            Plant.growth[bush] = Math.min(Plant.growth[bush] + 0.003, 1.0);
            
            // Log growth occasionally
            if (Math.random() < 0.02 && Plant.growth[bush] > oldGrowth) {
                console.log('[ECOSYSTEM] 🌿 Bush growing! Growth:', (Plant.growth[bush] * 100).toFixed(1) + '%');
            }
        }
        
        // Bushes produce berries when mature - more frequent
        if (Plant.growth[bush] > 0.7 && Math.random() < 0.01) {
            console.log('[ECOSYSTEM] 🫐 Bush produced berries! Animals can eat these.');
        }
        
        // Bushes provide shelter for small animals
        if (Math.random() < 0.005) {
            console.log('[ECOSYSTEM] 🌿 Bush providing shelter for small animals...');
        }
    }
    
    processFlowerBehavior(world, flower) {
        // Flowers grow quickly and attract pollinators
        if (Plant.growth && Plant.growth[flower] !== undefined) {
            const oldGrowth = Plant.growth[flower];
            Plant.growth[flower] = Math.min(Plant.growth[flower] + 0.004, 1.0);
            
            // Log growth occasionally
            if (Math.random() < 0.03 && Plant.growth[flower] > oldGrowth) {
                console.log('[ECOSYSTEM] 🌸 Flower growing! Growth:', (Plant.growth[flower] * 100).toFixed(1) + '%');
            }
        }
        
        // Flowers bloom and attract pollinators - more frequent
        if (Plant.growth[flower] > 0.5 && Math.random() < 0.02) {
            console.log('[ECOSYSTEM] 🌸🦋 Flower bloomed, attracting butterflies and bees!');
        }
        
        // Flowers spread pollen
        if (Plant.growth[flower] > 0.7 && Math.random() < 0.01) {
            console.log('[ECOSYSTEM] 🌸 Flower spreading pollen to nearby plants...');
        }
    }
    
    processMushroomBehavior(world, mushroom) {
        // Mushrooms grow in damp conditions and decompose matter
        if (Plant.growth && Plant.growth[mushroom] !== undefined) {
            Plant.growth[mushroom] = Math.min(Plant.growth[mushroom] + 0.0015, 1.0);
        }
        
        // Mushrooms release spores
        if (Plant.growth[mushroom] > 0.6 && Math.random() < 0.004) {
            console.log('[ECOSYSTEM] Mushroom released spores');
        }
        
        // Mushrooms decompose organic matter
        if (Math.random() < 0.002) {
            console.log('[ECOSYSTEM] Mushroom decomposing matter');
        }
    }
    
    processHerbBehavior(world, herb) {
        // Herbs grow quickly and have medicinal properties
        if (Plant.growth && Plant.growth[herb] !== undefined) {
            Plant.growth[herb] = Math.min(Plant.growth[herb] + 0.0025, 1.0);
        }
        
        // Herbs can be harvested for healing
        if (Plant.growth[herb] > 0.7 && Math.random() < 0.003) {
            console.log('[ECOSYSTEM] Herb ready for harvest');
        }
    }
    
    processCactusBehavior(world, cactus) {
        // Cacti grow slowly in dry conditions and store water
        if (Plant.growth && Plant.growth[cactus] !== undefined) {
            Plant.growth[cactus] = Math.min(Plant.growth[cactus] + 0.0003, 1.0);
        }
        
        // Cacti store water
        if (Plant.growth[cactus] > 0.5 && Math.random() < 0.001) {
            console.log('[ECOSYSTEM] Cactus storing water');
        }
        
        // Cacti bloom rarely but beautifully
        if (Plant.growth[cactus] > 0.9 && Math.random() < 0.0005) {
            console.log('[ECOSYSTEM] Cactus bloomed!');
        }
    }
    
    processPlantGrowth(world, plant, species) {
        // Growth rates vary by species
        const growthRates = {
            'TREE': 0.0005,
            'BUSH': 0.001,
            'FLOWER': 0.002,
            'MUSHROOM': 0.0015,
            'HERB': 0.0025,
            'CACTUS': 0.0003
        };
        
        const growthRate = growthRates[species] || 0.001;
        
        if (Plant.growth && Plant.growth[plant] !== undefined) {
            Plant.growth[plant] = Math.min(Plant.growth[plant] + growthRate, 1.0);
        }
        
        // Plant reproduction through seeds/spores
        if (Plant.growth[plant] > 0.8 && Math.random() < 0.0005) {
            console.log(`[ECOSYSTEM] ${species} reproducing`);
        }
        
        // Plant death from old age
        if (Plant.age && Plant.age[plant] !== undefined) {
            Plant.age[plant] += 1;
            const maxAge = this.getMaxPlantAge(species);
            if (Plant.age[plant] > maxAge) {
                console.log(`[ECOSYSTEM] ${species} died of old age`);
                // Could create dead plant entity here
            }
        }
    }
    
    getMaxPlantAge(species) {
        // Different plants have different lifespans
        const lifespans = {
            'TREE': 15000,     // Very long-lived
            'BUSH': 8000,      // Medium-lived
            'FLOWER': 3000,    // Short-lived
            'MUSHROOM': 2000,  // Very short-lived
            'HERB': 4000,      // Short-lived
            'CACTUS': 12000    // Long-lived
        };
        return lifespans[species] || 5000;
    }
    
    updateAnimalAI(world, animal) {
        // Full AI logic - delegate to existing animal system
        // This would call the existing animal AI logic
    }
    
    updateAnimalSimple(world, animal) {
        // Simplified movement only
        if (Math.random() < 0.1) {
            Animal.x[animal] += (Math.random() - 0.5) * TILE_SIZE * 0.5;
            Animal.y[animal] += (Math.random() - 0.5) * TILE_SIZE * 0.5;
        }
    }
    
    updateAnimalMinimal(world, animal) {
        // Almost no processing - just basic energy drain
        Animal.energy[animal] -= 0.01;
    }
    
    processInteractions(world) {
        const animals = animalQuery(world);
        this.interactions.processPredation(world, animals);
        this.interactions.processReproduction(world, animals);
        
        // Add environmental events
        this.processEnvironmentalEvents(world);
    }
    
    processEnvironmentalEvents(world) {
        // Random environmental events that make the world feel alive - MUCH MORE FREQUENT
        const eventChance = Math.random();
        
        if (eventChance < 0.05) { // 5% chance - much more frequent
            console.log('[ECOSYSTEM] 🌧️ Light rain starts falling - plants get water!');
            this.triggerRainEvent(world);
        } else if (eventChance < 0.10) { // 5% chance
            console.log('[ECOSYSTEM] 🌤️ Clouds pass overhead - providing shade');
            this.triggerCloudEvent(world);
        } else if (eventChance < 0.15) { // 5% chance
            console.log('[ECOSYSTEM] 🍃 Wind blows through the trees - spreading seeds');
            this.triggerWindEvent(world);
        } else if (eventChance < 0.20) { // 5% chance
            console.log('[ECOSYSTEM] 🦋 Butterflies flutter by - pollinating flowers');
            this.triggerButterflyEvent(world);
        } else if (eventChance < 0.25) { // 5% chance
            console.log('[ECOSYSTEM] 🐦 Birds sing in the distance - ecosystem is healthy!');
            this.triggerBirdSongEvent(world);
        }
    }
    
    triggerRainEvent(world) {
        // Rain affects plants and animals - more dramatic effect
        const plants = plantQuery(world);
        let plantsAffected = 0;
        
        for (const plant of plants.slice(0, 20)) { // Affect more plants
            if (Plant.growth && Plant.growth[plant] !== undefined) {
                Plant.growth[plant] = Math.min(Plant.growth[plant] + 0.02, 1.0); // More growth
                plantsAffected++;
            }
        }
        
        console.log('[ECOSYSTEM] 🌧️ Rain helped', plantsAffected, 'plants grow faster!');
        
        // Animals seek shelter
        const animals = animalQuery(world);
        for (const animal of animals.slice(0, 5)) {
            if (Animal.energy && Animal.energy[animal] !== undefined) {
                Animal.energy[animal] -= 0.5; // Animals use energy to seek shelter
            }
        }
        
        console.log('[ECOSYSTEM] 🌧️ Animals are seeking shelter from the rain...');
    }
    
    triggerCloudEvent(world) {
        // Clouds provide temporary shade
        const animals = animalQuery(world);
        for (const animal of animals.slice(0, 3)) {
            Animal.energy[animal] += 1; // Shade provides relief
        }
    }
    
    triggerWindEvent(world) {
        // Wind spreads seeds and pollen
        const plants = plantQuery(world);
        for (const plant of plants.slice(0, 8)) {
            if (Plant.growth && Plant.growth[plant] > 0.5) {
                console.log('[ECOSYSTEM] Wind carries seeds from mature plants');
            }
        }
    }
    
    triggerButterflyEvent(world) {
        // Butterflies indicate healthy ecosystem
        const flowers = plantQuery(world);
        let flowerCount = 0;
        for (const flower of flowers) {
            if (Plant.type[flower] === 3) { // Flower type
                flowerCount++;
            }
        }
        
        if (flowerCount > 0) {
            console.log(`[ECOSYSTEM] 🦋 ${flowerCount} flowers attract butterflies`);
        }
    }
    
    triggerBirdSongEvent(world) {
        // Birds sing when ecosystem is balanced
        const animals = animalQuery(world);
        let birdCount = 0;
        for (const animal of animals) {
            if (Animal.type[animal] === 4) { // Hawk type
                birdCount++;
            }
        }
        
        if (birdCount > 0) {
            console.log(`[ECOSYSTEM] 🐦 ${birdCount} birds sing in the trees`);
        }
    }
    
    balancePopulations(world, census) {
        // Implement population balancing logic
        // Spawn or cull entities based on census data
    }
    
    getEcosystemStats() {
        return {
            activeChunks: this.chunkManager.activeChunks.size,
            totalAnimals: animalQuery(world).length,
            totalPlants: plantQuery(world).length,
            recentPredations: this.interactions.predationEvents.length,
            recentReproductions: this.interactions.reproductionEvents.length
        };
    }
}

// Export singleton instance
export const ecosystemSystem = new EcosystemSystem();
